import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/types';
import { useAllocationGroupsAutocomplete } from '@/hooks/autocomplete/useAllocationGroupsAutocomplete';
import { useBudgetGroupsAutocomplete } from '@/hooks/autocomplete/useBudgetGroupsAutocomplete';
import { useDistributorGroupsAutocomplete } from '@/hooks/autocomplete/useDistributorGroupsAutocomplete';

export type AuditLogGroupType = 'AllocationGroup' | 'BudgetGroup' | 'DistributorGroup';

export interface GroupNameAutocompleteProps {
  organizationId: number;
  groupType: AuditLogGroupType | '';
  value: string[];
  onChange: BaseMultiAutocompleteProps<string>['autocomplete']['onChange'];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  enabled?: boolean;
  width?: string | number;
}

function GroupNameAutocomplete({
  organizationId,
  groupType,
  value,
  onChange,
  label = 'Group Name',
  placeholder = 'Enter at least 3 characters to search',
  disabled = false,
  enabled = true,
  width,
}: GroupNameAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const allocationGroups = useAllocationGroupsAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: enabled && !disabled && groupType === 'AllocationGroup',
  });

  const budgetGroups = useBudgetGroupsAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: enabled && !disabled && groupType === 'BudgetGroup',
  });

  const distributorGroups = useDistributorGroupsAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: enabled && !disabled && groupType === 'DistributorGroup',
  });

  const options = useMemo(() => {
    const processNames = (names: (string | null | undefined)[]): string[] =>
      Array.from(new Set(names.filter((name): name is string => Boolean(name)))).sort((a, b) => a.localeCompare(b));

    switch (groupType) {
      case 'AllocationGroup':
        return processNames((allocationGroups.data ?? []).map((g) => g.name));
      case 'BudgetGroup':
        return processNames((budgetGroups.data ?? []).map((g) => g.name));
      case 'DistributorGroup':
        return processNames((distributorGroups.data ?? []).map((g) => g.distributorName));
      default:
        return [];
    }
  }, [allocationGroups.data, budgetGroups.data, distributorGroups.data, groupType]);

  const isLoading = allocationGroups.isLoading || budgetGroups.isLoading || distributorGroups.isLoading;

  const handleInputChange = useCallback((_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  }, []);

  const getOptionDisabled = useCallback((option: string) => value.includes(option), [value]);

  return (
    <BaseMultiAutocomplete<string>
      autocomplete={{
        options,
        value,
        inputValue,
        onInputChange: handleInputChange,
        onChange,
        filterOptions: (x) => x,
        getOptionDisabled,
        label,
        placeholder,
        loading: isLoading,
        disabled: disabled || !groupType,
        width,
      }}
    />
  );
}

export default memo(GroupNameAutocomplete);
